import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import {
  useUnitCategory,
  useUpdateUnitCategory,
  useCreateUnitFormula,
  useUpdateUnitFormula,
  useDeleteUnitFormulas,
} from '../../../hooks/admin/useUnitCategories';
import type { UnitCategoryUpdateRequest, UnitFormula } from '../../../types/admin';
import UnitFormulaEditDialog from '../../../components/admin/UnitFormulaEditDialog';
import UnitFormulaCreateDialog from '../../../components/admin/UnitFormulaCreateDialog';
import DeleteDialog from '../../../components/admin/DeleteDialog';

export default function UnitCategoryEdit() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ucatId = parseInt(id || '0');

  const { data, isLoading } = useUnitCategory(ucatId);
  const updateCategory = useUpdateUnitCategory();
  const createFormula = useCreateUnitFormula();
  const updateFormula = useUpdateUnitFormula();
  const deleteFormulas = useDeleteUnitFormulas();

  const [selectedFormula, setSelectedFormula] = useState<UnitFormula | null>(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [selectedFormulaIds, setSelectedFormulaIds] = useState<number[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const { register, handleSubmit, formState: { errors, isDirty }, reset } = useForm<UnitCategoryUpdateRequest>({
    values: data?.category ? {
      nameLong: data.category.nameLong,
      nameShort: data.category.nameShort,
      baseUnitId: data.category.baseUnitId,
      userUnitId: data.category.userUnitId,
      userUnitPrecision: data.category.userUnitPrecision,
      description: data.category.description,
    } : undefined,
  });

  const onSubmit = async (formData: UnitCategoryUpdateRequest) => {
    try {
      await updateCategory.mutateAsync({ id: ucatId, data: formData });
      // Reset form with saved data to clear dirty state
      reset(formData);
    } catch (error) {
      console.error('Failed to update unit category:', error);
    }
  };

  const handleFormulaRowClick = (formula: UnitFormula) => {
    setSelectedFormula(formula);
    setShowEditDialog(true);
  };

  const handleCreateFormula = async (formData: any) => {
    try {
      await createFormula.mutateAsync({ categoryId: ucatId, data: formData });
    } catch (error) {
      console.error('Failed to create formula:', error);
      throw error;
    }
  };

  const handleUpdateFormula = async (formData: any) => {
    if (!selectedFormula) return;
    try {
      await updateFormula.mutateAsync({ id: selectedFormula.untfrmId, data: formData });
    } catch (error) {
      console.error('Failed to update formula:', error);
      throw error;
    }
  };

  const handleDeleteFormulas = async () => {
    try {
      await deleteFormulas.mutateAsync(selectedFormulaIds);
      setSelectedFormulaIds([]);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error('Failed to delete formulas:', error);
    }
  };

  const handleSelectAllFormulas = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked && data?.formulas) {
      setSelectedFormulaIds(data.formulas.map(f => f.untfrmId));
    } else {
      setSelectedFormulaIds([]);
    }
  };

  const handleSelectFormula = (untfrmId: number, checked: boolean, e: React.MouseEvent) => {
    e.stopPropagation();
    if (checked) {
      setSelectedFormulaIds([...selectedFormulaIds, untfrmId]);
    } else {
      setSelectedFormulaIds(selectedFormulaIds.filter(id => id !== untfrmId));
    }
  };

  const allFormulasSelected = data?.formulas && data.formulas.length > 0 && selectedFormulaIds.length === data.formulas.length;
  const someFormulasSelected = selectedFormulaIds.length > 0 && selectedFormulaIds.length < (data?.formulas.length || 0);

  if (isLoading) return <div>Loading unit category...</div>;
  if (!data) return <div>Unit category not found</div>;

  return (
    <div>
      <h1 className="text-3xl font-bold mb-6">Edit Unit Category</h1>

      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Unit Category ID:</span>
            <span className="ml-2 font-medium">{data.category.ucatId}</span>
          </div>
          <div>
            <span className="text-gray-600">Base Unit ID:</span>
            <span className="ml-2 font-medium">{data.category.baseUnitId}</span>
          </div>
          <div>
            <span className="text-gray-600">User Unit ID:</span>
            <span className="ml-2 font-medium">{data.category.userUnitId}</span>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium mb-2">Name Long *</label>
            <input
              type="text"
              {...register('nameLong', { required: 'Long name is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            {errors.nameLong && (
              <p className="text-red-600 text-sm mt-1">{errors.nameLong.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Name Short *</label>
            <input
              type="text"
              {...register('nameShort', { required: 'Short name is required' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            {errors.nameShort && (
              <p className="text-red-600 text-sm mt-1">{errors.nameShort.message}</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Base Unit ID *</label>
              <input
                type="number"
                {...register('baseUnitId', {
                  required: 'Base unit ID is required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'Base unit ID must be at least 1' },
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              {errors.baseUnitId && (
                <p className="text-red-600 text-sm mt-1">{errors.baseUnitId.message}</p>
              )}
              {data?.category.baseUnitName && (
                <p className="text-gray-600 text-sm mt-1">Current: {data.category.baseUnitName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">User Unit ID *</label>
              <input
                type="number"
                {...register('userUnitId', {
                  required: 'User unit ID is required',
                  valueAsNumber: true,
                  min: { value: 1, message: 'User unit ID must be at least 1' },
                })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              {errors.userUnitId && (
                <p className="text-red-600 text-sm mt-1">{errors.userUnitId.message}</p>
              )}
              {data?.category.userUnitName && (
                <p className="text-gray-600 text-sm mt-1">Current: {data.category.userUnitName}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">User Unit Precision *</label>
            <input
              type="number"
              {...register('userUnitPrecision', {
                required: 'Precision is required',
                valueAsNumber: true,
                min: { value: 0, message: 'Precision must be 0 or greater' },
              })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
            {errors.userUnitPrecision && (
              <p className="text-red-600 text-sm mt-1">{errors.userUnitPrecision.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Description</label>
            <textarea
              {...register('description')}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            />
          </div>

          <div className="flex gap-4 justify-end">
            <button
              type="submit"
              disabled={!isDirty || updateCategory.isPending}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {updateCategory.isPending ? 'Saving...' : 'Save Changes'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/admin/units')}
              className="px-6 py-2 bg-gray-200 hover:bg-gray-300 rounded-lg"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>

      {/* Related Formulas Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Unit Formulas</h2>
          <div className="flex gap-3">
            <button
              onClick={() => setShowCreateDialog(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              Create New Unit
            </button>
            {selectedFormulaIds.length > 0 && (
              <button
                onClick={() => setShowDeleteDialog(true)}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
              >
                Delete Units ({selectedFormulaIds.length})
              </button>
            )}
          </div>
        </div>

        {data.formulas.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={allFormulasSelected}
                      ref={input => {
                        if (input) input.indeterminate = someFormulasSelected;
                      }}
                      onChange={handleSelectAllFormulas}
                      className="rounded border-gray-300"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Formula ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Unit ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Name Long</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Name Short</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Base to Disp</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Disp to Base</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-600 uppercase">Notes</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {data.formulas.map((formula) => (
                  <tr
                    key={formula.untfrmId}
                    onClick={() => handleFormulaRowClick(formula)}
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-3 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={selectedFormulaIds.includes(formula.untfrmId)}
                        onChange={(e) => handleSelectFormula(formula.untfrmId, e.target.checked, e as any)}
                        className="rounded border-gray-300"
                      />
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <span className="text-blue-600 font-medium">{formula.untfrmId}</span>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">{formula.unitId}</td>
                    <td className="px-4 py-3">{formula.nameLong}</td>
                    <td className="px-4 py-3 whitespace-nowrap">{formula.nameShort}</td>
                    <td className="px-4 py-3 font-mono text-sm">{formula.baseToDisp}</td>
                    <td className="px-4 py-3 font-mono text-sm">{formula.dispToBase}</td>
                    <td className="px-4 py-3">{formula.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-gray-600">No formulas defined for this unit category.</p>
        )}
      </div>

      {/* Dialogs */}
      <UnitFormulaEditDialog
        formula={selectedFormula}
        isOpen={showEditDialog}
        onClose={() => {
          setShowEditDialog(false);
          setSelectedFormula(null);
        }}
        onSave={handleUpdateFormula}
        isSaving={updateFormula.isPending}
      />

      <UnitFormulaCreateDialog
        isOpen={showCreateDialog}
        onClose={() => setShowCreateDialog(false)}
        onSave={handleCreateFormula}
        isSaving={createFormula.isPending}
      />

      <DeleteDialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleDeleteFormulas}
        title="Delete Unit Formulas"
        message={`Are you sure you want to delete ${selectedFormulaIds.length} unit formula${selectedFormulaIds.length === 1 ? '' : 's'}? This action cannot be undone.`}
        isLoading={deleteFormulas.isPending}
      />
    </div>
  );
}
